import React from 'react';
import { X } from 'lucide-react';

/**
 * Panel showing stats for ALL countries - accessible via button
 */
export default function AllCountryStatsPanel({ countries, onClose }) {
  const getEnrichmentColor = (enrichment) => {
    if (enrichment >= 100) return 'text-red-500';
    if (enrichment >= 75) return 'text-orange-500';
    if (enrichment >= 50) return 'text-yellow-500';
    return 'text-green-500';
  };

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/60 z-50"
        onClick={onClose}
      />
      
      {/* Stats Panel - Centered */}
      <div 
        className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-50 w-full max-w-4xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="bunker-panel p-6 bg-slate-900/98 backdrop-blur-lg border-4 border-amber-500 shadow-2xl">
          {/* Header */}
          <div className="flex items-center justify-between mb-6 pb-4 border-b-2 border-neutral-700">
            <h2 className="text-3xl font-bold text-amber-400 uppercase tracking-wider">
              📊 Country Intelligence
            </h2>
            <button
              onClick={onClose}
              className="w-10 h-10 bg-red-600 hover:bg-red-700 text-white rounded-full flex items-center justify-center transition-all"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Countries Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {countries.map((country) => (
              <div
                key={country.code}
                className="bg-slate-800/50 p-4 rounded-lg border-2 hover:bg-slate-700/50 transition-all"
                style={{
                  borderColor: country.color,
                  boxShadow: `0 0 15px ${country.color}30`
                }}
              >
                {/* Country Header */}
                <div className="flex items-center justify-between mb-3 pb-2 border-b border-neutral-700">
                  <div>
                    <h3 
                      className="text-lg font-bold"
                      style={{ color: country.color }}
                    >
                      {country.name}
                    </h3>
                    <p className="text-xs text-neutral-400">{country.leader_title}</p>
                  </div>
                  {country.is_player && (
                    <span className="text-xs bg-blue-600 px-2 py-1 rounded font-bold">YOU</span>
                  )}
                </div>

                {/* Resources */}
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-neutral-400">👥 Population</span>
                    <span className="text-white font-bold">{country.resources.population}M</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-neutral-400">💰 Funds</span>
                    <span className="text-white font-bold">${country.resources.money}B</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-neutral-400">⚔️ Military</span>
                    <span className="text-white font-bold">{country.resources.military}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-neutral-400">📈 Influence</span>
                    <span className="text-white font-bold">{country.resources.influence}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-neutral-400">☢️ Nukes</span>
                    <span className="text-white font-bold">{country.resources.nukes}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-neutral-400">⚡ Enrichment</span>
                    <span className={`font-bold ${getEnrichmentColor(country.resources.uranium_enrichment)}`}>
                      {country.resources.uranium_enrichment}%
                      {country.resources.uranium_enrichment >= 100 && ' 🚀'}
                    </span>
                  </div>
                </div>

                {/* Threat Level */}
                {!country.is_player && country.resources.uranium_enrichment >= 90 && (
                  <div className="mt-3 pt-3 border-t border-neutral-700">
                    <span className="text-xs bg-red-600 px-2 py-1 rounded animate-pulse">
                      ⚠️ HIGH THREAT
                    </span>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Footer Hint */}
          <div className="mt-6 pt-4 border-t-2 border-neutral-700 text-center">
            <p className="text-sm text-neutral-400">
              Click anywhere outside or press X to close
            </p>
          </div>
        </div>
      </div>
    </>
  );
}
